<template>
  <div v-if="show" class="modal-overlay" @click="cancelCreateTag">
    <div class="modal-dialog" @click.stop>
      <div class="modal-header">
        <h3>Tag This Version</h3>
        <button class="close-btn" type="button" @click="cancelCreateTag">&times;</button>
      </div>
      <div class="modal-body">
        <div class="form-group">
          <label for="tag-create-name">Tag Name</label>
          <input
            id="tag-create-name"
            v-model="newTag.name"
            type="text"
            placeholder="e.g., v1.0.0"
            class="form-input"
            @input="handleTagNameInput"
          >
          <div v-if="tagNameError" class="form-error">{{ tagNameError }}</div>
        </div>
        <div class="form-group">
          <label for="tag-create-summary">Tag Summary</label>
          <textarea
            id="tag-create-summary"
            v-model="newTag.summary"
            placeholder="Describe this version..."
            rows="4"
            class="form-input"
          ></textarea>
        </div>
      </div>
      <div class="modal-footer">
        <div v-if="isCreatingTag" class="create-status">
          <span class="create-status__spinner"></span>
          <span>Creating Tag</span>
        </div>
        <div class="modal-actions">
          <button
            class="btn btn-cancel"
            type="button"
            :disabled="isCreatingTag"
            @click="cancelCreateTag"
          >
            Cancel
          </button>
          <button
            class="btn btn-create"
            type="button"
            :disabled="isCreatingTag || !isTagNameValid"
            @click="createTag"
          >
            {{ isCreatingTag ? 'Creating...' : 'Create Tag' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { useToast } from 'vue-toast-notification';
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  datasetId: {
    type: [Number, String],
    required: true,
  },
});

const emit = defineEmits(['update:show', 'created']);
const toast = useToast({
  position: 'top',
});

const isCreatingTag = ref(false);
const newTag = ref({
  name: '',
  summary: '',
});
const tagNameError = ref('');

const isTagNameValid = computed(() => {
  const tagName = newTag.value.name.trim();
  if (!tagName) return false;
  return /^[a-zA-Z0-9.-]+$/.test(tagName);
});

function handleTagNameInput(event) {
  const value = event.target.value;
  const allowedChars = /^[a-zA-Z0-9.-]*$/;

  if (!allowedChars.test(value)) {
    tagNameError.value = 'Tag name can only contain letters, numbers, dots (.), and dashes (-)';
  } else {
    tagNameError.value = '';
  }
}

function resetCreateTag() {
  newTag.value = {
    name: '',
    summary: '',
  };
  tagNameError.value = '';
}

function cancelCreateTag() {
  emit('update:show', false);
  resetCreateTag();
}

async function createTag() {
  const tagName = newTag.value.name.trim();
  const tagSummary = newTag.value.summary.trim();

  if (!tagName || !tagSummary) {
    toast.error('Please fill in both Tag Name and Tag Summary');
    return;
  }

  isCreatingTag.value = true;
  try {
    const dsConn = new DatastoreConnect();
    const response = await dsConn.api('version.tag.create', {
      dataset_id: parseInt(props.datasetId, 10),
      name: tagName,
      description: tagSummary,
    });

    if (response?.error) {
      toast.error(`Failed to create tag: ${response.error.message}`);
      return;
    }

    toast.success('Tag created successfully');
    emit('created', {
      response,
      tagName,
    });
    cancelCreateTag();
  } catch (error) {
    console.error('Error creating tag:', error);
    toast.error(`Failed to create tag: ${error.message || error}`);
  } finally {
    isCreatingTag.value = false;
  }
}
</script>

<style lang="scss" scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.modal-dialog {
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  width: 90%;
  max-width: 500px;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #ddd;
}

.modal-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
}

.close-btn {
  background: none;
  border: none;
  font-size: 28px;
  cursor: pointer;
  color: #666;
  line-height: 1;
  padding: 0;
  width: 30px;
  height: 30px;
}

.close-btn:hover {
  color: #000;
}

.modal-body {
  padding: 20px;
}

.form-group {
  margin-bottom: 16px;
}

.form-group label {
  display: block;
  margin-bottom: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #333;
}

.form-input {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 13px;
  font-family: inherit;
}

.form-input:focus {
  outline: none;
  border-color: #4caf50;
  box-shadow: 0 0 0 2px rgba(76, 175, 80, 0.1);
}

textarea.form-input {
  resize: vertical;
}

.modal-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 16px 20px;
  border-top: 1px solid #ddd;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-left: auto;
}

.create-status {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #333;
  font-size: 13px;
  font-weight: 600;
}

.create-status__spinner {
  width: 16px;
  height: 16px;
  border: 2px solid #c8e6c9;
  border-top-color: #4caf50;
  border-radius: 50%;
  animation: create-tag-spin 0.8s linear infinite;
}

@keyframes create-tag-spin {
  to {
    transform: rotate(360deg);
  }
}

.btn {
  padding: 8px 18px;
  border: none;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.btn-cancel {
  background: #f0f0f0;
  color: #333;
}

.btn-cancel:hover {
  background: #e5e5e5;
}

.btn-create {
  background: #4caf50;
  color: #fff;
}

.btn-create:hover {
  background: #43a047;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.form-error {
  margin-top: 4px;
  font-size: 12px;
  color: #d32f2f;
}
</style>